/***
 *
 *   NOTIFICATION
 *   Banner notification that appears at the top of the screen.
 *   Create a notification anytime using context.notification.show()
 *   and pass the props below
 *
 *   PROPS
 *   message: string containing the message
 *   type - success/error/warning
 *   autoclose - if false, the notification won't disapear until clicking the X
 *
 **********/

import { useRef } from 'react';
import { Icon } from 'components/lib';
import { CSSTransition } from 'react-transition-group';
import './notification.scss';
import { UMButton } from '@urbanmetrix/um-kit';
import type { NotificationData } from 'components/view/view';

interface NotificationProps {
  data?: NotificationData | null;
  onClose?: () => void;
}

export function Notification({ data, onClose }: NotificationProps) {
  // React 19 removed ReactDOM.findDOMNode, which CSSTransition uses by default;
  // a nodeRef makes it target the child directly instead.
  const nodeRef = useRef(null);

  if (!data) return null;

  let _class = 'notification';
  if (data.format) _class += ` ${data.format}`;

  return (
    <CSSTransition in appear nodeRef={nodeRef} timeout={0} classNames={_class}>
      <div
        ref={nodeRef}
        className={`notification ${data.type || ''} ${data.format || ''} ${data.icon ? 'with-icon' : ''}`}
      >
        {data.icon && <Icon size={19} color="dark" image={data.icon} />}

        <p>{data.text}</p>

        {!data.autoclose && (
          <UMButton
            className="btn-close-notification"
            icon="x"
            color={data.format === 'toast' ? 'dark' : 'light'}
            onClick={onClose}
          />
        )}
      </div>
    </CSSTransition>
  );
}
